import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../contexts/AuthContext';
import { useGlobalSettings } from '../hooks/useGlobalSettings';
import { useT, useLanguage } from '../i18n';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { PhoneField, Country, DEFAULT_COUNTRY, joinPhone } from '../components/PhoneField';
import { CONTACT_DISPLAY, whatsappLink } from '../contact';

// Aceita e-mail ou número de WhatsApp: contas criadas no site entram pelo número
// As mensagens do formulário são chaves de tradução, resolvidas na hora de mostrar
// O número vem do PhoneField, fora do formulário; aqui só validamos o e-mail quando ele é usado
const loginSchema = z.object({
  email: z.string().optional(),
  senha: z.string().min(6, 'login.passwordMin'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
  const t = useT();
  const { language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // O número é o caminho normal: quem se cadastra pelo site nem tem e-mail no sistema
  const [mode, setMode] = useState<'phone' | 'email'>('phone');
  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY);
  const [phoneNumber, setPhoneNumber] = useState('');
  // O link de cadastro só aparece quando o dono da plataforma abre as inscrições
  const [signupEnabled, setSignupEnabled] = useState(false);

  useEffect(() => {
    fetch('/api/auth/signup/status')
      .then(response => response.json())
      .then(data => setSignupEnabled(!!data.enabled))
      .catch(() => setSignupEnabled(false));
  }, []);
  const { login } = useAuth();
  const { settings } = useGlobalSettings();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    const identifier = mode === 'phone' ? joinPhone(country, phoneNumber) : data.email;
    if (!identifier) return;

    setIsSubmitting(true);
    try {
      await login(identifier, data.senha);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden">
        <div className="flex flex-col lg:flex-row">
          <div className="lg:w-1/2 p-12 flex items-center justify-center text-white" style={{ background: 'linear-gradient(135deg, #233e4f 0%, #1a2d3b 100%)' }}>
            <div className="text-center">
              <div className="w-24 h-24 bg-white/20 rounded-2xl mx-auto mb-8 flex items-center justify-center backdrop-blur-sm">
                {settings?.iconUrl ? (
                  <img
                    src={settings.iconUrl}
                    alt="System Icon"
                    className="w-16 h-16 object-contain"
                    style={{ filter: 'brightness(0) invert(1)' }}
                  />
                ) : (
                  <img
                    src="/favicon.png"
                    alt=""
                    className="w-16 h-16 object-contain"
                    style={{ filter: 'brightness(0) invert(1)' }}
                  />
                )}
              </div>
              <h1 className="text-4xl font-bold mb-4">
                {settings?.pageTitle || ''}
              </h1>
              <p className="text-xl text-gray-100 mb-8">
                {t('login.tagline')}
              </p>
              <p className="text-gray-200 max-w-md">
                {t('login.blurb')}
              </p>
            </div>
          </div>

          <div className="lg:w-1/2 p-12">
            <div className="max-w-md mx-auto">
              <div className="text-center mb-8">
                <div className="flex justify-end mb-2">
                  <LanguageSwitcher className="text-gray-500 hover:text-gray-800 hover:bg-gray-100" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">{t('login.welcome')}</h2>
                <p className="text-gray-600">{t('login.subtitle')}</p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                  <label htmlFor="identifier" className="block text-sm font-medium text-gray-700 mb-2">
                    {mode === 'phone' ? t('login.phone') : t('login.email')}
                  </label>

                  {mode === 'phone' ? (
                    <PhoneField
                      id="identifier"
                      country={country}
                      number={phoneNumber}
                      onCountryChange={setCountry}
                      onNumberChange={setPhoneNumber}
                      language={language}
                      disabled={isSubmitting}
                    />
                  ) : (
                    <input
                      {...register('email')}
                      type="email"
                      id="identifier"
                      dir="ltr"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-gray-500"
                      placeholder="you@email.com"
                      disabled={isSubmitting}
                    />
                  )}

                  {mode === 'email' && errors.email && (
                    <p className="mt-2 text-sm text-red-600">{t(errors.email.message || '')}</p>
                  )}

                  <button
                    type="button"
                    onClick={() => setMode(mode === 'phone' ? 'email' : 'phone')}
                    className="mt-2 text-xs text-gray-500 hover:text-gray-800 underline"
                  >
                    {mode === 'phone' ? t('login.useEmail') : t('login.usePhone')}
                  </button>
                </div>

                <div>
                  <label htmlFor="senha" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('login.password')}
                  </label>
                  <input
                    {...register('senha')}
                    type="password"
                    id="senha"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                    style={{
                      '--tw-ring-color': '#233e4f'
                    } as React.CSSProperties}
                    onFocus={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.style.outline = 'none';
                      target.style.borderColor = '#233e4f';
                      target.style.boxShadow = '0 0 0 2px rgba(35, 62, 79, 0.2)';
                    }}
                    onBlur={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.style.borderColor = '#d1d5db';
                      target.style.boxShadow = 'none';
                    }}
                    placeholder="••••••••"
                    disabled={isSubmitting}
                  />
                  {errors.senha && (
                    <p className="mt-2 text-sm text-red-600">{t(errors.senha.message || '')}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || (mode === 'phone' && phoneNumber.length < 6)}
                  className="w-full text-white py-3 px-6 rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(135deg, #233e4f 0%, #1a2d3b 100%)',
                    '--tw-ring-color': '#233e4f'
                  } as React.CSSProperties}
                  onMouseEnter={(e) => {
                    const target = e.target as HTMLButtonElement;
                    target.style.background = 'linear-gradient(135deg, #1a2d3b 0%, #0f1a23 100%)';
                  }}
                  onMouseLeave={(e) => {
                    const target = e.target as HTMLButtonElement;
                    target.style.background = 'linear-gradient(135deg, #233e4f 0%, #1a2d3b 100%)';
                  }}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      {t('login.submitting')}
                    </div>
                  ) : (
                    t('login.submit')
                  )}
                </button>
              </form>

              {signupEnabled ? (
                <p className="text-center text-sm text-gray-500 mt-6">
                  {t('login.newHere')}{' '}
                  <Link to="/signup" className="font-medium" style={{ color: '#233e4f' }}>{t('common.createAccount')}</Link>
                </p>
              ) : (
                // Inscrições fechadas: em vez de deixar a pessoa sem saída, ela fala com a equipe
                <p className="text-center text-sm text-gray-500 mt-6">
                  {t('login.noAccount')}{' '}
                  <a
                    href={whatsappLink(t('login.contactPrefill'))}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-emerald-700 hover:text-emerald-900"
                  >
                    {t('login.contactUs')} <span dir="ltr">{CONTACT_DISPLAY}</span>
                  </a>
                </p>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}